import { useEffect, useMemo, useState } from 'react';
import { cachedGet, REFERENCE_URLS } from '../offline/offlineApi';
import { applyRoleSettings, normalizeRoleSettings } from '../util/participantGroups';

// Role settings (Orga/Admin: display name, dot color, event leadership, salary per led event) — one shared
// list for the whole app, same pattern as useParticipantGroups: fetched at most once per page load and
// kept in this module; offline through the reference cache (prefetched on the home page).
// Publishing also fills the role registry in util/participantGroups, so roleLabel()/roleColorKey()/
// groupRoleLabel() return the configured values everywhere. Components that render role names or colors
// call this hook so they re-render once the settings arrive.
let shared = null;      // normalized list once loaded
let pending = null;     // in-flight promise
const listeners = new Set();

function publish(list) {
  shared = list;
  applyRoleSettings(list);
  listeners.forEach(fn => fn(list));
}

function load() {
  if (shared) return Promise.resolve(shared);
  if (!pending) {
    pending = cachedGet(REFERENCE_URLS.roleSettings)
      .then(data => {
        const list = normalizeRoleSettings(data);
        publish(list);
        return list;
      })
      .finally(() => { pending = null; });
  }
  return pending;
}

// After saving a role in /groups: replace the shared list so every mounted consumer re-renders.
export function setRoleSettings(rawList) {
  publish(normalizeRoleSettings(rawList));
}

// Home page prefetch (offline copy): runs through the shared loader, so it reuses the request of a
// mounted consumer instead of fetching in parallel.
export function preloadRoleSettings() {
  return load().catch(() => null);
}

// Back online after "Erneut verbinden": load again and publish to every mounted consumer. On failure the
// previous settings stay in place and the next consumer retries.
export function reloadRoleSettings() {
  shared = null;
  return load().catch(() => null);
}

/**
 * Role settings for display and the role editor.
 * - roles: settings in role order (Orga, Admin) — empty until loaded
 * - byRole: Map 'Organizer' | 'Admin' → setting
 * - loaded / failed: load state (until loaded, the role helpers fall back to the seeded defaults)
 */
export default function useRoleSettings() {
  const [list, setList] = useState(shared);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const listener = (next) => {
      if (!alive) return;
      setList(next);
      setFailed(false);
    };
    listeners.add(listener);
    if (shared) setList(shared);
    else load().catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      listeners.delete(listener);
    };
  }, []);

  return useMemo(() => {
    const roles = list ?? [];
    return {
      roles,
      byRole: new Map(roles.map(r => [r.role, r])),
      loaded: list != null,
      failed,
    };
  }, [list, failed]);
}
